
import React, { useContext, useEffect, useMemo, useState } from "react";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import ReactApexChart from "react-apexcharts";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import { getFieldAgentActivityCount, fetchFieldAgentMap } from "../../service/Task";
import { filterAgentsByCurrentUserGroups } from "../../components/task/GroupAssignParts";
import ReactSelector from "../../components/common/CommonReactSelector";
import { components } from "react-select";
import { FaChartColumn } from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const StatusOption = (props) => (
  <components.Option {...props}>
    <span className="flex items-center gap-2">
      <span
        className="w-2 h-4 rounded shrink-0"
        style={{ backgroundColor: getStatusColor(props.data?.value) }}
      />
      {props.label}
    </span>
  </components.Option>
);

const StatusSingleValue = ({ data, ...props }) => (
  <components.SingleValue {...props} data={data}>
    <span className="flex items-center gap-2">
      <span
        className="w-2 h-4 rounded shrink-0"
        style={{ backgroundColor: getStatusColor(data?.value) }}
      />
      {data?.label}
    </span>
  </components.SingleValue>
);

const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "#4379F2";
    case "In Progress":
    case "In_Progress":
      return "#FFA500";
    case "Assigned":
      return "#F3C623";
    case "Completed":
      return "#6EC207";
    case "Finished":
      return "#10B981";
    case "On_Hold":
      return "#FF3333";
    default:
      return "#A0A0A0";
  }
};

const getDefaultFromDate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
};
const getDefaultToDate = () => {
  const d = new Date();
    d.setHours(0, 0, 0, 0);
  return d;
};

const BottomPanel = ({
  isOpen,
  onToggle,
  initialExpanded = false,
  selectedFieldAgentId,
  onFieldAgentChange,
  onAnalyticsStatusChange,
  showDetailPanel,
  mapType,
  setActivityAnalyticsDate,
}) => {
  const {
    langMode,
    projectId,
    activityId,
  } = useContext(AppContext);
  const [open, setOpen] = useState(initialExpanded);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const STATUS_OPTIONS = useMemo(() => {
    const tr = translations[langMode];
    return [
      { value: "", label: tr.all },
      { value: "In_Progress", label: tr.statusInProgressLabel },
      { value: "Assigned", label: tr.statusAssignedLabel },
      { value: "Completed", label: tr.statusCompletedLabel },
    ];
  }, [langMode]);

  const [selectedStatus, setSelectedStatus] = useState(() => ({
    value: "",
    label: translations[langMode].all,
  }));

  useEffect(() => {
    setSelectedStatus((prev) => {
      const match = STATUS_OPTIONS.find((o) => o.value === (prev?.value ?? ""));
      return match ?? STATUS_OPTIONS[0];
    });
  }, [STATUS_OPTIONS]);
  const [selectedFieldAgent, setSelectedFieldAgent] = useState(null);
  const [fieldAgentList, setFieldAgentList] = useState([]);
  const [fieldAgentListLoading, setFieldAgentListLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getDefaultFromDate);
  const [toDate, setToDate] = useState(getDefaultToDate);
  const [activityCountData, setActivityCountData] = useState([]);
  const [activityCountLoading, setActivityCountLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setActivityAnalyticsDate?.({ fromDate, toDate });
  }, [isOpen, fromDate, toDate, setActivityAnalyticsDate]);

  // If parent sets it to true, sync the expanded state
  useEffect(() => {
    if (isOpen === true) {
      // Panel is visible, use the initialExpanded prop to set state
      setOpen(initialExpanded);
    }
  }, [isOpen, initialExpanded]);

  // Activity is selected in MapView (top-right); use activityId from context for chart
  const chartActivityId = useMemo(() => {
    if (activityId == null) return null;
    if (typeof activityId === "string") return activityId.split(",")[0]?.trim() || activityId;
    if (Array.isArray(activityId) && activityId.length > 0) return String(activityId[0]);
    return String(activityId);
  }, [activityId]);

  // Fetch field agents for dropdown (same API as DetailPanel: fetchFieldAgentMap)
  useEffect(() => {
    const loadAgents = async () => {
      if (!activityId) {
        setFieldAgentList([]);
        return;
      }
      setFieldAgentListLoading(true);
      try {
        const response = await fetchFieldAgentMap(activityId);
        const list = Array.isArray(response?.body?.value)
          ? response.body.value
          : Array.isArray(response)
            ? response
            : [];
        // Only show field agents who belong to a group assigned to this user
        // (blank group list => show all).
        setFieldAgentList(await filterAgentsByCurrentUserGroups(list));
      } catch (error) {
        console.error("Error fetching field agents:", error);
        setFieldAgentList([]);
      } finally {
        setFieldAgentListLoading(false);
      }
    };
    loadAgents();
  }, [activityId]);

  // Field agent dropdown options (from fetchFieldAgentMap - same as DetailPanel)
  const fieldAgentDropdownOptions = useMemo(() => {
    const all = [{ value: "", label: translations[langMode].allFieldAgents }];
    (fieldAgentList || []).forEach((agent) => {
      const id = agent.userId ?? agent.id;
      const name = agent.name ?? agent.userName ?? agent.firstName ?? `Agent ${id}`;
      if (id != null) all.push({ value: String(id), label: name });
    });
    return all;
  }, [fieldAgentList, langMode]);

  const handleFieldAgentChange = (option) => {
    setSelectedFieldAgent(option);
    if (onFieldAgentChange) onFieldAgentChange(option?.value ?? "");
  };

  // Sync dropdown value from parent when parent-controlled (e.g. map filter)
  const fieldAgentSelectValue = useMemo(() => {
    if (selectedFieldAgent != null) return selectedFieldAgent;
    if (selectedFieldAgentId !== undefined && selectedFieldAgentId !== null && fieldAgentDropdownOptions.length > 0) {
      const match = fieldAgentDropdownOptions.find((o) => o.value === String(selectedFieldAgentId));
      return match ?? { value: "", label: translations[langMode].allFieldAgents };
    }
    return null;
  }, [selectedFieldAgent, selectedFieldAgentId, fieldAgentDropdownOptions, langMode]);

  // Effective field agent for API: local selection takes precedence, then parent's selectedFieldAgentId
  const effectiveFieldAgentId = selectedFieldAgent?.value != null && selectedFieldAgent.value !== ""
    ? selectedFieldAgent.value
    : (selectedFieldAgentId != null && selectedFieldAgentId !== "" ? String(selectedFieldAgentId) : null);
  const effectiveFieldAgentName = selectedFieldAgent?.label
    ?? (effectiveFieldAgentId && fieldAgentDropdownOptions.length
      ? fieldAgentDropdownOptions.find((o) => o.value === effectiveFieldAgentId)?.label
      : null);

  // Fetch field agent activity count: initially projectId + workflowId only; status and other filters optional
  useEffect(() => {
    const fetchActivityCount = async () => {
      const hasValidWorkflow = chartActivityId != null && chartActivityId !== "" && !Number.isNaN(Number(chartActivityId));
      if (!isOpen || !projectId || !hasValidWorkflow) {
        if (!isOpen) return;
        setActivityCountData([]);
        return;
      }
      setActivityCountLoading(true);
      try {
        const params = {
          projectId,
          workflowId: chartActivityId,
        };
        if (selectedStatus?.value != null && selectedStatus.value !== "") params.taskStatus = selectedStatus.value;
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (effectiveFieldAgentId != null && effectiveFieldAgentId !== "") params.fieldAgentId = effectiveFieldAgentId;
        if (effectiveFieldAgentName != null && effectiveFieldAgentName !== "") params.fieldAgentName = effectiveFieldAgentName;
        const data = await getFieldAgentActivityCount(params);
        setActivityCountData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching activity count:", error);
        setActivityCountData([]);
      } finally {
        setActivityCountLoading(false);
      }
    };
    fetchActivityCount();
  }, [isOpen, projectId, chartActivityId, selectedStatus?.value, fromDate, toDate, effectiveFieldAgentId, effectiveFieldAgentName]);

  // Flatten field agents from API response for Field Agent dropdown
  const fieldAgentOptions = useMemo(() => {
    const all = [{ value: "", label: "All Field Agents" }];
    const seen = new Set();
    (activityCountData || []).forEach((item) => {
      (item.fieldAgents || []).forEach((fa) => {
        if (fa.fieldAgentId != null && !seen.has(fa.fieldAgentId)) {
          seen.add(fa.fieldAgentId);
          all.push({ value: fa.fieldAgentId, label: fa.fieldAgentName || `Agent ${fa.fieldAgentId}` });
        }
      });
    });
    return all;
  }, [activityCountData]);

  // X-axis: unique field agents (by id); same agent can appear multiple times with different statuses
  const areaChartCategories = useMemo(() => {
    const item = activityCountData?.[0];
    const fieldAgents = item?.fieldAgents || [];
    const seen = new Set();
    const categories = [];
    fieldAgents.forEach((fa) => {
      const id = fa.fieldAgentId;
      if (id != null && !seen.has(id)) {
        seen.add(id);
        categories.push(fa.fieldAgentName || `Agent ${id}`);
      }
    });
    return categories;
  }, [activityCountData]);

  // One series per status; each series has taskCount per field agent; colors match status
  const areaChartSeries = useMemo(() => {
    const item = activityCountData?.[0];
    const fieldAgents = item?.fieldAgents || [];
    if (!item || !fieldAgents.length) return [];

    const agentIds = [...new Set(fieldAgents.map((fa) => fa.fieldAgentId).filter((id) => id != null))];
    const statusOrder = ["Assigned", "Active", "In_Progress", "In Progress", "Completed", "Finished", "On_Hold"];
    const statuses = [...new Set(fieldAgents.map((fa) => fa.status).filter(Boolean))].sort(
      (a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b)
    );

    return statuses.map((status) => ({
      name: status,
      data: agentIds.map(
        (agentId) =>
          fieldAgents
            .filter((fa) => fa.fieldAgentId === agentId && fa.status === status)
            .reduce((sum, fa) => sum + (fa.taskCount ?? 0), 0)
      ),
    }));
  }, [activityCountData]);

  const areaChartColors = useMemo(
    () => areaChartSeries.map((s) => getStatusColor(s.name)),
    [areaChartSeries]
  );

  const handleToggle = () => {
    const newState = !open;
    setOpen(newState);
    if (onToggle) {
      onToggle(newState);
    }
  };

  const handleClearAll = () => {
    setSelectedStatus({ value: "", label: "All" });
    setSelectedFieldAgent(null);
    setFromDate(getDefaultFromDate());
    setToDate(getDefaultToDate());
  };



  const areaChartOptions = useMemo(
    () => ({
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: false },
        stacked: true,
      },
      plotOptions: {
        area: {
          stacking: "normal",
        },
      },
      legend: {
        position: "top",
        horizontalAlign: isMobile ? "center" : "right",
        fontSize: isMobile ? "11px" : "12px",
        height: isMobile ? 48 : undefined,
        itemMargin: {
          horizontal: isMobile ? 5 : 10,
          vertical: isMobile ? 2 : 4,
        },
        labels: {
          colors: "#6B7280",
        },
        markers: {
          width: isMobile ? 8 : 10,
          height: isMobile ? 8 : 10,
          radius: isMobile ? 8 : 10,
        },
      },
      colors: areaChartColors.length > 0 ? areaChartColors : ["#3B82F6", "#22C55E"],
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        borderColor: "#E5E7EB",
        strokeDashArray: 4,
        padding: {
          left: isMobile ? 2 : 10,
          right: isMobile ? 4 : 10,
          top: 0,
          bottom: isMobile ? 4 : 0,
        },
      },
      xaxis: {
        categories: ["6AM", "8AM", "10AM", "12PM", "2PM", "4PM", "6PM"],
        tickAmount: isMobile ? 4 : undefined,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          rotate: isMobile ? -30 : 0,
          rotateAlways: isMobile,
          hideOverlappingLabels: true,
          trim: true,
          maxHeight: isMobile ? 52 : 120,
          formatter: isMobile
            ? (value) => {
                const text = String(value ?? "");
                return text.length > 10 ? `${text.slice(0, 9)}…` : text;
              }
            : undefined,
          style: {
            fontSize: isMobile ? "9px" : "11px",
            colors: "#6B7280",
          },
        },
      },
      yaxis: {
        min: 0,
        tickAmount: 4,
        labels: {
          minWidth: isMobile ? 22 : undefined,
          maxWidth: isMobile ? 28 : undefined,
          style: {
            fontSize: isMobile ? "9px" : "11px",
            colors: "#6B7280",
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
      },
    }),
    [areaChartColors, isMobile]
  );

  const panelHeight = open ? (isMobile ? "min(64dvh, 500px)" : "260px") : "42px";

  return (
    <div
      className="absolute left-0 right-0 bottom-0"
      style={{
        transform: isOpen ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        pointerEvents: isOpen ? "auto" : "none",
        visibility: isOpen ? "visible" : "hidden",
        width: showDetailPanel && !isMobile ? "calc(100% - 400px)" : "100%",
        zIndex: isOpen ? 10 : 0,
      }}
    >
      <div
        className={`backdrop-blur-md shadow-lg flex flex-col ${mapType === "satellite" ? 'bg-[#fffffff2] backdrop-blur-md' : 'bg-[#ffffffcc]/80 backdrop-blur-md'}`}
        style={{
          height: panelHeight,
          transition: "height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-4 h-[42px] min-h-[42px] shrink-0 border-b border-gray-200">
          <span
            className="gap-2 sm:gap-3 text-sm cursor-pointer flex items-center font-medium text-gray-800"
            onClick={handleToggle}
          >
            <span className="bg-blue-500/30 rounded-md p-1">
              <FaChartColumn size={16} className="text-blue-500" />
            </span>
            <span>{translations[langMode].activityAnalytics}</span>
          </span>
          <button
            type="button"
            onClick={handleToggle}
            className="p-1 rounded-md text-gray-500 hover:bg-gray-100 min-h-[32px] min-w-[32px] flex items-center justify-center transition-colors"
            aria-label={open ? "Collapse panel" : "Expand panel"}
          >
            {open ? <FaChevronDown size={14} /> : <FaChevronUp size={14} />}
          </button>
        </div>

        {/* Body */}
        <div
          className="p-3 sm:p-4 flex flex-col md:flex-row gap-3 md:gap-4 flex-1 min-h-0 overflow-y-auto md:overflow-visible"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`flex flex-col gap-2.5 sm:gap-3 w-full md:w-[230px] shrink-0 md:min-h-0 md:max-h-full md:overflow-y-auto md:overflow-x-hidden app-scrollbar ${
              langMode === "ar" || langMode === "ur" ? "md:pl-2" : "md:pr-2"
            }`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-2.5">
              <div className="w-full">
                <label className="block text-xs text-gray-500 mb-1">
                  {translations[langMode].analyticsStatusLabel}
                </label>
                <ReactSelector
                  options={STATUS_OPTIONS}
                  value={selectedStatus}
                  onChange={(status) => {
                    setSelectedStatus(status);
                    onAnalyticsStatusChange?.(status?.value ?? "");
                  }}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.value}
                  styles={{
                    option: (base, state) => {
                      const { data, isSelected, isFocused } = state;
                      let backgroundColor = "transparent";
                      let color = "#334155";

                      if (isSelected && isFocused) {
                        backgroundColor = "#f1f5f9";
                        color = "#1f2937";
                      } else if (isSelected) {
                        backgroundColor = "#f8fafc";
                        color = "#1f2937";
                      } else if (isFocused) {
                        backgroundColor = "#f1f5f9";
                        color = "#1f2937";
                      }

                      return {
                        ...base,
                        backgroundColor,
                        color,
                        borderLeft: `3px solid ${getStatusColor(data?.value)}`,
                        paddingLeft: 8,
                        cursor: "pointer",
                      };
                    },
                    singleValue: (base, { data }) => ({
                      ...base,
                      borderLeft: `3px solid ${getStatusColor(data?.value)}`,
                      paddingLeft: 8,
                      color: "#334155",
                    }),
                  }}
                  components={{
                    Option: StatusOption,
                    SingleValue: StatusSingleValue,
                  }}
                  height="36px"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  menuPlacement="top"
                />
              </div>

              <div className="w-full">
                <label className="block text-xs text-gray-500 mb-1">{translations[langMode].fieldAgent}</label>
                <ReactSelector
                  options={fieldAgentDropdownOptions}
                  value={fieldAgentSelectValue}
                  onChange={handleFieldAgentChange}
                  getOptionLabel={(opt) => opt?.label ?? ""}
                  getOptionValue={(opt) => opt?.value ?? ""}
                  placeholder={fieldAgentListLoading ? translations[langMode].chartLoading : translations[langMode].select}
                  isClearable={true}
                  isSearchable={true}
                  isLoading={fieldAgentListLoading}
                  height="36px"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  menuPlacement="top"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
              <div className="w-full">
                <label className="block text-xs text-gray-500 mb-1">
                  {translations[langMode].fromdate}
                </label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => setFromDate(date)}
                  className="w-full h-9 px-2 rounded-[10px] text-sm border border-gray-300 bg-white"
                  calendarClassName="!rounded-[10px] shadow-lg"
                  popperPlacement="top-start"
                  popperContainer={({ children }) => (
                    <div
                      style={{
                        position: "fixed",
                        zIndex: 100000,
                      }}
                    >
                      {children}
                    </div>
                  )}
                />
              </div>

              <div className="w-full">
                <label className="block text-xs text-gray-500 mb-1">
                  {translations[langMode].todate}
                </label>
                <DatePicker
                  selected={toDate}
                  onChange={(date) => setToDate(date ?? null)}
                  className="w-full h-9 px-2 rounded-[10px] text-sm border border-gray-300 bg-white"
                  calendarClassName="rounded-[10px] shadow-lg"
                  popperPlacement="top-start"
                  popperContainer={({ children }) => (
                    <div
                      style={{
                        position: "fixed",
                        zIndex: 100000,
                      }}
                    >
                      {children}
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Right: Area chart */}
          <div className="flex min-h-[230px] w-full min-w-0 flex-1 flex-col justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50/50 p-1.5 sm:p-2 md:min-h-0">
            {activityCountLoading ? (
              <div className="h-full min-h-[140px] flex items-center justify-center text-gray-500 text-sm">
                {translations[langMode].chartLoading}
              </div>
            ) : areaChartCategories.length > 0 && areaChartSeries.length > 0 ? (
              <ReactApexChart
                options={{
                  ...areaChartOptions,
                  xaxis: {
                    ...areaChartOptions.xaxis,
                    categories: areaChartCategories,
                  },
                }}
                series={areaChartSeries}
                type="area"
                height={isMobile ? 220 : 180}
              />
            ) : (
              <div className="h-full min-h-[140px] flex items-center justify-center text-gray-400 text-xs sm:text-sm text-center px-4">
                {translations[langMode].chartSelectFiltersHint}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BottomPanel;
